import { useQuery } from '@tanstack/react-query';
import { createFileRoute, Link } from '@tanstack/react-router';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Award, Download } from 'lucide-react';
import { certificationService } from '@/features/certification/services/certification.service';
import { usePermissions } from '@/common/hooks/usePermissions';
import { Permission } from '@/common/enums/permissions.enum';

export const Route = createFileRoute(
  '/_authenticated/certification/certificados/$certificateId',
)({
  component: CertificatePage,
});

function CertificatePage() {
  const { certificateId } = Route.useParams();
  const { hasPermission } = usePermissions();
  const canView = hasPermission(Permission.CERTIFICATION_VIEW);

  const { data: certificate, isLoading } = useQuery({
    queryKey: ['certification', 'certificate-detail', certificateId],
    queryFn: () => certificationService.getCertificate(certificateId),
    enabled: canView && !!certificateId,
  });

  const { data: downloadData } = useQuery({
    queryKey: ['certification', 'certificate-download', certificateId],
    queryFn: () => certificationService.getCertificateDownloadUrl(certificateId),
    enabled: canView && !!certificateId && !!certificate?.pdfStorageKey,
  });

  const { data: progress } = useQuery({
    queryKey: ['certification', 'progress', certificate?.courseId],
    queryFn: () => certificationService.getProgress(certificate!.courseId),
    enabled: canView && !!certificate?.courseId,
  });

  const progressPct = progress?.progressPct ?? 100;

  if (!canView) {
    return (
      <div className="p-6">
        <p className="text-muted-foreground">Sem permissão para acessar este certificado.</p>
      </div>
    );
  }

  if (isLoading || !certificate) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[300px]">
        <div className="animate-pulse text-muted-foreground">Carregando...</div>
      </div>
    );
  }

  const downloadUrl = downloadData?.url;

  return (
    <div className="p-6 max-w-xl mx-auto space-y-6">
      <Card>
        <CardHeader className="text-center">
          <Award className="h-16 w-16 mx-auto text-primary mb-2" />
          <h1 className="text-2xl font-semibold">Certificado</h1>
          <p className="text-muted-foreground text-sm">
            Este certificado atesta a conclusão do curso.
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Barra de progresso */}
          <div className="space-y-2">
            <div className="flex justify-between items-center">
              <p className="text-sm text-muted-foreground">Progresso do curso</p>
              <p className="text-sm font-medium">{progressPct}%</p>
            </div>
            <Progress value={progressPct} className="h-3" />
            {progress && (
              <p className="text-xs text-muted-foreground">
                {progress.completedCount} de {progress.totalStages} etapas concluídas
              </p>
            )}
          </div>
          <div>
            <p className="text-sm text-muted-foreground">Emitido em</p>
            <p className="font-medium">
              {new Date(certificate.issuedAt).toLocaleDateString('pt-BR', {
                day: '2-digit',
                month: 'long',
                year: 'numeric',
              })}
            </p>
          </div>
          <div>
            <p className="text-sm text-muted-foreground">Código de validação</p>
            <p className="font-mono font-medium">{certificate.validationCode}</p>
          </div>
          {downloadUrl && (
            <Button asChild className="w-full">
              <a href={downloadUrl} target="_blank" rel="noopener noreferrer">
                <Download className="h-4 w-4 mr-2" />
                Baixar PDF
              </a>
            </Button>
          )}
          {!downloadUrl && certificate.pdfStorageKey && (
            <p className="text-sm text-muted-foreground">
              PDF em processamento. Tente novamente em instantes.
            </p>
          )}
          <Button variant="outline" asChild className="w-full">
            <Link to="/certification">Voltar à lista de cursos</Link>
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
